import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listingService, requestService } from '../services';
import ListingDetailHeader from '../components/ListingDetailHeader';
import ListingDetailContent from '../components/ListingDetailContent';
import ListingActionSidebar from '../components/ListingActionSidebar';
import MapView from '../components/MapView';

// Keep track of failed image URLs globally to prevent repeated retries
// eslint-disable-next-line no-unused-vars
const failedImageUrls = new Set();

function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Helper function to consistently get a valid image URL from listing data
  const getListingImageUrl = (listing) => {
    if (!listing) return '/images/placeholder-food.png';
    
    // Debug the image data structure
    console.log(`DEBUG - Detail page listing ${listing.id} image data:`, { 
      imageUrl: listing.imageUrl, 
      images: listing.images,
      imageType: listing.images ? typeof listing.images : 'undefined'
    });
    
    // Helper function to only convert http to https without rewriting domains
    const fixImageUrl = (url) => {
      if (!url) return '/images/placeholder-food.png';
      
      // Test URL for example.com that may be seed data
      if (url.includes('example.com')) {
        console.log('Detected example.com URL, using placeholder');
        return '/images/placeholder-food.png';
      }
      
      // Convert HTTP to HTTPS for secure loading, but don't rewrite domains
      if (url.startsWith('http:')) {
        url = url.replace('http:', 'https:');
      }
      
      // Just use relative URLs as is
      if (url.startsWith('/api/images/')) {
        return `${window.location.origin}${url}`;
      }
      
      // Return the URL directly without domain rewriting
      return url;
    };
    
    // Check for imageUrl field first (this might be set by the backend)
    if (listing.imageUrl) {
      return fixImageUrl(listing.imageUrl);
    }
    
    // Handle case where images field is present
    if (listing.images) {
      // If images is a JSON string, parse it
      if (typeof listing.images === 'string') {
        try {
          // Direct string URL (not in array format)
          if (!listing.images.startsWith('[') && 
              (listing.images.startsWith('http') || 
               listing.images.includes('wastenot-api') ||
               listing.images.includes('wastenot-storage'))) {
            return fixImageUrl(listing.images);
          }
            
          if (listing.images.startsWith('[')) {
            // Try to parse as JSON array
            const imageArray = JSON.parse(listing.images);
            if (Array.isArray(imageArray) && imageArray.length > 0) {
              const imageUrl = imageArray[0];
              return fixImageUrl(imageUrl);
            }
          } else if (listing.images.includes('/api/images/') || 
                     listing.images.includes('wastenot-api.tinkacode.workers.dev')) {
            return fixImageUrl(listing.images);
          } else if (listing.images.startsWith('listings/')) {
            return fixImageUrl(listing.images);
          }
        } catch (e) {
          console.log(`Image parse error for listing ${listing.id}:`, e);
          // If parsing fails, just use the string directly
          if (listing.images.includes('/api/images/') || 
              listing.images.includes('wastenot-api.tinkacode.workers.dev')) {
            return fixImageUrl(listing.images);
          }
          if (listing.images.includes('wastenot-storage.wastenot.org')) {
            return fixImageUrl(listing.images);
          }
          if (listing.images.startsWith('listings/')) {
            return fixImageUrl(listing.images);
          }
        }
      }
      
      // If images is already an array
      if (Array.isArray(listing.images) && listing.images.length > 0) {
        const imageUrl = listing.images[0];
        return fixImageUrl(imageUrl);
      }
    }
    
    // Fallback to placeholder
    return '/images/placeholder-food.png';
  };

  // Fetch listing data when component mounts or id changes
  useEffect(() => {
    const fetchListing = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Use listingService to fetch the listing details
        const response = await listingService.getListingById(id);
        setListing(response.listing);
      } catch (err) {
        console.error('Error fetching listing:', err);
        setError('Failed to load this listing. It may have been removed or is no longer available.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchListing();
  }, [id, user]);

  // Handle back button
  const handleBack = () => {
    navigate('/listings');
  };

  // Handle food request
  const handleRequestSubmit = async (requestData) => {
    try {
      // Use requestService to create a new request
      const response = await requestService.createRequest({
        listing_id: id,
        ...requestData
      });
      
      return response;
    } catch (err) {
      console.error('Error submitting request:', err);
      throw err;
    }
  };

  // Handle status update
  const handleStatusUpdate = async (listingId, status) => {
    try {
      // Use listingService to update the listing status
      const response = await listingService.updateListing(listingId, { status });
      
      // Update the local listing state with the new status
      setListing(prev => ({ ...prev, status: response.listing.status }));
      
      return response;
    } catch (err) {
      console.error('Error updating status:', err);
      throw err;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-start pt-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading listing details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-start pt-16">
        <div className="text-center max-w-md mx-auto">
          <svg className="h-12 w-12 mx-auto text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="mt-3 text-lg font-medium text-gray-900">Listing Not Found</h3>
          <p className="mt-2 text-gray-600">{error}</p>
          <div className="mt-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
            >
              Go back to listings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <ListingDetailHeader listing={listing} onBack={handleBack} />
      
      <div className="mt-6 lg:grid lg:grid-cols-3 lg:gap-8">
        {/* Main content */}
        <div className="lg:col-span-2">
          {/* Image */}
          {getListingImageUrl(listing) !== '/images/placeholder-food.png' ? (
            <div className="aspect-w-16 aspect-h-9 mb-6 rounded-lg overflow-hidden shadow-md">
              <img 
                src={getListingImageUrl(listing)}
                alt={listing.title} 
                className="w-full h-full object-cover"
                crossOrigin="anonymous" 
                referrerPolicy="no-referrer"
                loading="lazy"
                onError={(e) => {
                  console.log(`Image load error for listing ${listing.id}:`, {
                    src: e.target.src,
                    listing: listing.id
                  });
                  
                  // Add failed URL to our tracking set
                  failedImageUrls.add(e.target.src);
                  
                  e.target.onerror = null; // Prevent infinite loops
                  e.target.src = '/images/placeholder-food.png';
                }}
              />
            </div>
          ) : (
            <div className="aspect-w-16 aspect-h-9 mb-6 flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          
          {/* Content */}
          <ListingDetailContent listing={listing} />
          
          {/* Map */}
          <div className="mt-8">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Location</h2>
            {listing?.location?.lat && listing?.location?.lng ? (
              <MapView 
                listings={[listing]} 
                height="300px"
              />
            ) : (
              <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center text-gray-500">
                No location data available
              </div>
            )}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="mt-6 lg:mt-0">
          <ListingActionSidebar 
            listing={listing}
            onRequestSubmit={handleRequestSubmit}
            onStatusUpdate={handleStatusUpdate}
          />
        </div>
      </div>
    </div>
  );
}

export default ListingDetailPage;
